/**
 * State for action-feedback toasts (GH-935).
 *
 * Client code raises toasts directly (toastStore.success/error) after an
 * action completes; BrandToaster also promotes server flash props into
 * toasts so feedback survives redirects and full page loads.
 */

import { defineStore } from "pinia";
import { ref } from "vue";

const AUTO_DISMISS_MS = 4500;
const MAX_VISIBLE = 3;

let nextId = 1;

export const useToastStore = defineStore("toast", () => {
    const toasts = ref([]);

    function push(type, message) {
        const id = nextId++;

        toasts.value.push({ id, type, message, paused: false });

        // Oldest toast collapses out once the stack is full.
        if (toasts.value.length > MAX_VISIBLE) {
            toasts.value.shift();
        }

        scheduleDismiss(id);

        return id;
    }

    function scheduleDismiss(id) {
        setTimeout(() => {
            const toast = toasts.value.find((candidate) => candidate.id === id);

            // Hovered toasts wait for another full cycle before retrying.
            if (toast?.paused) {
                scheduleDismiss(id);

                return;
            }

            dismiss(id);
        }, AUTO_DISMISS_MS);
    }

    function dismiss(id) {
        toasts.value = toasts.value.filter((toast) => toast.id !== id);
    }

    function pause(id, isPaused) {
        const toast = toasts.value.find((candidate) => candidate.id === id);

        if (toast) {
            toast.paused = isPaused;
        }
    }

    const success = (message) => push("success", message);
    const error = (message) => push("error", message);

    // --- Full-page-load handoff. Flows that leave via window.location can't
    //     keep in-memory state; flashNext parks the toast in sessionStorage
    //     and BrandToaster drains it on mount.
    const FLASH_KEY = "gh:toast";

    function flashNext(type, message) {
        try {
            sessionStorage.setItem(
                FLASH_KEY,
                JSON.stringify({ type, message }),
            );
        } catch {
            // Storage unavailable (private mode quotas) — drop the toast.
        }
    }

    function drainFlashed() {
        try {
            const raw = sessionStorage.getItem(FLASH_KEY);

            if (!raw) {
                return;
            }

            sessionStorage.removeItem(FLASH_KEY);

            const { type, message } = JSON.parse(raw);
            push(type, message);
        } catch {
            // Malformed payload — nothing to show.
        }
    }

    return { toasts, success, error, dismiss, pause, flashNext, drainFlashed };
});
